import { Breadcrumb, Grid, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { orgCardShadow, orgHeroGradient } from './organisationsUi';

type BreadcrumbItem = {
  title: ReactNode;
};

type StatItem = {
  label: string;
  value: string | number;
};

type OrgPageHeroProps = {
  breadcrumbItems?: BreadcrumbItem[];
  title: string;
  subtitle?: string;
  roleLabel?: string;
  stats?: StatItem[];
  action?: ReactNode;
};

export function OrgPageHero({
  breadcrumbItems,
  title,
  subtitle,
  roleLabel,
  stats,
  action,
}: OrgPageHeroProps) {
  const screens = Grid.useBreakpoint();
  const isCompact = !screens.md;

  return (
    <section
      style={{
        background: orgHeroGradient,
        borderRadius: 8,
        boxShadow: orgCardShadow,
        padding: isCompact ? '16px 16px' : '20px 24px',
        marginBottom: 16,
      }}
    >
      {breadcrumbItems?.length ? (
        <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 14 }} />
      ) : null}
      <div
        style={{
          display: 'flex',
          flexDirection: isCompact ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isCompact ? 'stretch' : 'flex-start',
          gap: 16,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <Typography.Title level={2} style={{ margin: 0, lineHeight: 1.15, wordBreak: 'break-word' }}>
            {title}
          </Typography.Title>
          {subtitle ? (
            <Typography.Paragraph
              type="secondary"
              style={{ marginBottom: 0, marginTop: 10, maxWidth: 640, lineHeight: 1.5 }}
            >
              {subtitle}
            </Typography.Paragraph>
          ) : null}
          {roleLabel ? (
            <Tag color="blue" style={{ marginTop: 12, marginRight: 0 }}>
              {roleLabel}
            </Tag>
          ) : null}
        </div>
        {action ? <div style={{ flexShrink: 0 }}>{action}</div> : null}
      </div>
      {stats?.length ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isCompact
              ? '1fr'
              : stats.length === 1
                ? 'minmax(160px, 240px)'
                : `repeat(${Math.min(stats.length, 3)}, minmax(0, 1fr))`,
            gap: 10,
            marginTop: 14,
          }}
        >
          {stats.map((stat) => (
            <div
              key={stat.label}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                background: 'rgba(255, 255, 255, 0.82)',
                boxShadow: orgCardShadow,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <Typography.Text
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  color: '#98a2b3',
                }}
              >
                {stat.label}
              </Typography.Text>
              <Typography.Text style={{ fontSize: 22, fontWeight: 700, color: '#101828' }}>
                {stat.value}
              </Typography.Text>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
